import React, { useState, useRef, useEffect } from 'react';
import './TestCaseForm.css';
import api from '../api';

export default function TestCaseForm({
  testCase,
  suites,
  onSubmit,
  onCancel,
  loading,
  error,
  projectId,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: testCase?.title || '',
    description: testCase?.description || '',
    precondition: testCase?.precondition || '',
    steps:
      testCase && Array.isArray(testCase.steps)
        ? testCase.steps.join('\n')
        : testCase?.steps || '',
    expectedResult: testCase?.expectedResult || '',
    testSuite:
      testCase && typeof testCase.testSuite === 'object'
        ? testCase.testSuite._id || testCase.testSuite.id
        : testCase?.testSuite || '',
    linkedRequirements: testCase?.linkedRequirements || [],
  });

  const [formErrors, setFormErrors] = useState({});
  
  // Suite search state
  const [suiteSearchTerm, setSuiteSearchTerm] = useState('');
  const [suiteSearchOpen, setSuiteSearchOpen] = useState(false);
  const [filteredSuites, setFilteredSuites] = useState(suites || []);
  const suiteSearchRef = useRef(null);
  
  // Requirement search state
  const [requirementSearchTerm, setRequirementSearchTerm] = useState('');
  const [requirementSearchOpen, setRequirementSearchOpen] = useState(false);
  const [availableRequirements, setAvailableRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const requirementSearchRef = useRef(null);

  // Initialize selected suite name for display
  const selectedSuiteName = formData.testSuite
    ? suites.find(s => (s._id || s.id) === formData.testSuite)?.name || ''
    : '';

  // Initialize selected requirements data
  const selectedRequirementsData = formData.linkedRequirements
    .map(reqId => availableRequirements.find(r => (r._id || r.id) === reqId))
    .filter(Boolean);

  // Load requirements on component mount
  useEffect(() => {
    if (projectId) {
      loadRequirements();
    }
  }, [projectId]);

  // Keep filteredSuites in sync when suites prop arrives / changes
  useEffect(() => {
    const term = suiteSearchTerm.trim().toLowerCase();
    if (term) {
      setFilteredSuites(suites.filter(s => s.name.toLowerCase().includes(term)));
    } else {
      setFilteredSuites(suites || []);
    }
  }, [suiteSearchTerm, suites]);

  // Update requirement search filter
  useEffect(() => {
    const term = requirementSearchTerm.trim().toLowerCase();
    if (term) {
      setFilteredRequirements(
        availableRequirements.filter(
          r =>
            r.title?.toLowerCase().includes(term) ||
            r.key?.toLowerCase().includes(term)
        )
      );
    } else {
      setFilteredRequirements(availableRequirements);
    }
  }, [requirementSearchTerm, availableRequirements]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (suiteSearchRef.current && !suiteSearchRef.current.contains(e.target)) {
        setSuiteSearchOpen(false);
      }
      if (requirementSearchRef.current && !requirementSearchRef.current.contains(e.target)) {
        setRequirementSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRequirements = async () => {
    try {
      const res = await api.get('/requirements', { params: { projectId, limit: 500 } });
      const reqs = res.data?.requirements || res.data?.data || res.data || [];
      setAvailableRequirements(Array.isArray(reqs) ? reqs : []);
    } catch (err) {
      console.error('Failed to load requirements:', err);
    }
  };

  const validateStep = (stepNum) => {
    const errors = {};
    
    if (stepNum === 1) {
      if (!formData.title.trim()) {
        errors.title = 'Summary is required';
      }
      // Test Suite is now optional - no error if not selected
    }
    
    return errors;
  };

  const handleNextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length === 0) {
      setFormErrors({});
      setCurrentStep(currentStep + 1);
    } else {
      setFormErrors(errors);
    }
  };

  const handlePrevStep = () => {
    setFormErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errors = validateStep(1);
    if (Object.keys(errors).length === 0) {
      // Trim and limit title to 200 characters
      const trimmedTitle = formData.title.trim().substring(0, 200);
      
      onSubmit({
        ...formData,
        title: trimmedTitle,
        steps: formData.steps.split('\n').filter(s => s.trim()),
      });
    } else {
      setFormErrors(errors);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const stepsArray = formData.steps
    .split('\n')
    .filter(s => s.trim())
    .map((step, idx) => ({
      number: idx + 1,
      text: step.trim(),
    }));

  const totalSteps = 4;

  return (
    <div className="tcf-page-container">
      {/* Breadcrumb / Navigation */}
      <div className="tcf-breadcrumb">
        <button className="tcf-back-btn" onClick={onCancel}>
          ← Back
        </button>
        <div className="tcf-breadcrumb-text">
          Test Cases / <span className="fw-semibold">{testCase ? 'Edit' : 'Create'}</span>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="tcf-step-indicator">
        <div className="tcf-steps-progress">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className={`tcf-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}>
              <div className="tcf-step-circle">{step}</div>
              <div className="tcf-step-label">
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Steps'}
                {step === 3 && 'Expected Result'}
                {step === 4 && 'Review'}
              </div>
            </div>
          ))}
        </div>
        <div className="tcf-progress-bar">
          <div className="tcf-progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tcf-main-content">
        <form onSubmit={handleSubmit} className="tcf-form">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-4">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="tcf-step-content">
              <h2 className="tcf-content-title">Basic Information</h2>
              <p className="tcf-content-subtitle">Define the summary, test suite, and linked requirements for your test case</p>

              <div className="tcf-section">
                <div className="tcf-section-header">
                  <h3 className="tcf-section-title">Required Fields</h3>
                </div>
                <div className="tcf-section-content">
                  <div className="tcf-field-group">
                    <label className="tcf-field-label">Summary *</label>
                    <input
                      type="text"
                      className={`tcf-field-input ${
                        formErrors.title ? 'is-invalid' : ''
                      }`}
                      maxLength="200"
                      value={formData.title}
                      onChange={e => handleInputChange('title', e.target.value)}
                      placeholder="e.g., User Login with Valid Credentials"
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Max 50 characters. Only letters, numbers, spaces, and underscores (_) are allowed.
                    </small>
                    {formData.title.length >= 50 && (
                      <small className="text-warning d-block">
                        Character limit reached ({formData.title.length}/50)
                      </small>
                    )}
                    {formErrors.title && (
                      <small className="text-danger">{formErrors.title}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="tcf-section">
                <div className="tcf-section-header">
                  <h3 className="tcf-section-title">Optional Fields</h3>
                </div>
                <div className="tcf-section-content">
                  {/* Test Suite Search */}
                  <div className="tcf-field-group">
                    <label className="tcf-field-label">Test Suite</label>
                    <div ref={suiteSearchRef} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="tcf-field-input"
                        placeholder="Search test suites..."
                        value={suiteSearchOpen ? suiteSearchTerm : selectedSuiteName}
                        onChange={e => {
                          setSuiteSearchTerm(e.target.value);
                          setSuiteSearchOpen(true);
                        }}
                        onFocus={() => setSuiteSearchOpen(true)}
                      />
                      {suiteSearchOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          marginTop: '4px'
                        }}>
                          {filteredSuites.length > 0 ? (
                            filteredSuites.map(suite => (
                              <div
                                key={suite._id || suite.id}
                                onClick={() => {
                                  handleInputChange('testSuite', suite._id || suite.id);
                                  setSuiteSearchTerm('');
                                  setSuiteSearchOpen(false);
                                }}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  background: formData.testSuite === (suite._id || suite.id) ? '#f5f5f5' : 'white'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={e => e.currentTarget.style.background = formData.testSuite === (suite._id || suite.id) ? '#f5f5f5' : 'white'}
                              >
                                {suite.name}
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '10px 12px', color: '#999' }}>No suites found</div>
                          )}
                        </div>
                      )}
                    </div>
                    {formData.testSuite && (
                      <small
                        style={{ cursor: 'pointer', color: '#0066cc' }}
                        onClick={() => {
                          handleInputChange('testSuite', '');
                          setSuiteSearchTerm('');
                        }}
                      >
                        Clear selection
                      </small>
                    )}
                  </div>

                  {/* Linked Requirements Search */}
                  <div className="tcf-field-group">
                    <label className="tcf-field-label">Link Requirements</label>
                    <div ref={requirementSearchRef} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="tcf-field-input"
                        placeholder="Search requirements..."
                        value={requirementSearchTerm}
                        onChange={e => {
                          setRequirementSearchTerm(e.target.value);
                          setRequirementSearchOpen(true);
                        }}
                        onFocus={() => setRequirementSearchOpen(true)}
                      />
                      {requirementSearchOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '250px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          marginTop: '4px'
                        }}>
                          {filteredRequirements.length > 0 ? (
                            filteredRequirements.map(req => {
                              const isSelected = formData.linkedRequirements.includes(req._id || req.id);
                              return (
                                <div
                                  key={req._id || req.id}
                                  onClick={() => {
                                    if (isSelected) {
                                      handleInputChange(
                                        'linkedRequirements',
                                        formData.linkedRequirements.filter(id => id !== (req._id || req.id))
                                      );
                                    } else {
                                      handleInputChange(
                                        'linkedRequirements',
                                        [...formData.linkedRequirements, req._id || req.id]
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    background: isSelected ? '#e3f2fd' : 'white',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    style={{ marginRight: '8px' }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{req.title}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>{req.key}</div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '10px 12px', color: '#999' }}>
                              {availableRequirements.length === 0 ? 'No requirements available' : 'No requirements found'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedRequirementsData.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <small className="text-muted">Selected Requirements:</small>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {selectedRequirementsData.map(req => (
                            <span
                              key={req._id || req.id}
                              style={{
                                background: '#e3f2fd',
                                border: '1px solid #90caf9',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {req.title}
                              <button
                                type="button"
                                onClick={() =>
                                  handleInputChange(
                                    'linkedRequirements',
                                    formData.linkedRequirements.filter(id => id !== (req._id || req.id))
                                  )
                                }
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#90caf9',
                                  fontSize: '14px',
                                  padding: 0
                                }}
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="tcf-field-group">
                    <label className="tcf-field-label">Description</label>
                    <textarea
                      className="tcf-field-input"
                      rows="5"
                      value={formData.description}
                      onChange={e =>
                        handleInputChange('description', e.target.value)
                      }
                      placeholder="Provide a detailed description of what this test case covers..."
                    />
                    <small className="tcf-form-hint">
                      {formData.description.length} characters
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Steps */}
          {currentStep === 2 && (
            <div className="tcf-step-content">
              <h2 className="tcf-content-title">Test Steps</h2>
              <p className="tcf-content-subtitle">Define the steps to execute this test case</p>

              {/* Precondition */}
              <div className="tcf-section">
                <div className="tcf-section-header">
                  <h3 className="tcf-section-title">Precondition <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.8rem' }}>(optional)</span></h3>
                </div>
                <div className="tcf-section-content">
                  <div className="tcf-field-group">
                    <textarea
                      className="tcf-field-input"
                      rows="2"
                      value={formData.precondition}
                      onChange={e => handleInputChange('precondition', e.target.value)}
                      placeholder="e.g., User is logged in. App is on the home screen."
                    />
                    <small className="tcf-form-hint">Describe any conditions that must be true before this test is executed.</small>
                  </div>
                </div>
              </div>

              <div className="tcf-section">
                <div className="tcf-section-header">
                  <h3 className="tcf-section-title">Test Steps</h3>
                  {stepsArray.length > 0 && (
                    <span className="tcf-step-count">{stepsArray.length} steps</span>
                  )}
                </div>
                <div className="tcf-section-content">
                  {stepsArray.length > 0 ? (
                    <div className="tcf-steps-table-wrapper">
                      <table className="tcf-steps-table">
                        <thead>
                          <tr>
                            <th style={{ width: '5%' }}>#</th>
                            <th>Step Description</th>
                            <th style={{ width: '20%' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stepsArray.map(step => (
                            <tr key={step.number}>
                              <td className="text-center">
                                <span className="tcf-step-badge">{step.number}</span>
                              </td>
                              <td>{step.text}</td>
                              <td>
                                <button
                                  type="button"
                                  className="tcf-action-btn delete-btn"
                                  onClick={() => {
                                    const updatedSteps = stepsArray
                                      .filter(s => s.number !== step.number)
                                      .map((s, idx) => s.text)
                                      .join('\n');
                                    handleInputChange('steps', updatedSteps);
                                  }}
                                  title="Delete step"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="tcf-empty-state">
                      <p>No steps added yet. Add steps below.</p>
                    </div>
                  )}

                  <div className="tcf-steps-input-group">
                    <label className="tcf-field-label mt-3">Add Steps (one per line)</label>
                    <textarea
                      className="tcf-field-input tcf-steps-textarea"
                      rows="8"
                      value={formData.steps}
                      onChange={e => handleInputChange('steps', e.target.value)}
                      placeholder={`Enter each step on a new line:\n1. Navigate to the login page\n2. Enter valid email address\n3. Enter valid password`}
                    />
                    <small className="tcf-form-hint">
                      Total: {stepsArray.length} step(s)
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Expected Result */}
          {currentStep === 3 && (
            <div className="tcf-step-content">
              <h2 className="tcf-content-title">Expected Result</h2>
              <p className="tcf-content-subtitle">Describe what should happen when the test passes</p>

              <div className="tcf-section">
                <div className="tcf-section-header">
                  <h3 className="tcf-section-title">Expected Outcome</h3>
                </div>
                <div className="tcf-section-content">
                  <div className="tcf-field-group">
                    <label className="tcf-field-label">Expected Result</label>
                    <textarea
                      className="tcf-field-input"
                      rows="10"
                      value={formData.expectedResult}
                      onChange={e =>
                        handleInputChange('expectedResult', e.target.value)
                      }
                      placeholder="Describe what should happen when the test passes successfully..."
                    />
                    <small className="tcf-form-hint">
                      {formData.expectedResult.length} characters
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="tcf-step-content">
              <h2 className="tcf-content-title">Review & Confirm</h2>
              <p className="tcf-content-subtitle">Please review your test case before submitting</p>

              <div className="tcf-review-container">
                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Summary</h5>
                  <p className="tcf-review-text">
                    {formData.title || <em>No summary provided</em>}
                  </p>
                </div>

                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Test Suite</h5>
                  <p className="tcf-review-text">
                    {formData.testSuite
                      ? suites.find(
                          s =>
                            (s._id || s.id) === formData.testSuite
                        )?.name || 'Unknown'
                      : <em>Not selected</em>}
                  </p>
                </div>

                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Linked Requirements</h5>
                  {selectedRequirementsData.length > 0 ? (
                    <ul className="tcf-review-list">
                      {selectedRequirementsData.map(req => (
                        <li key={req._id || req.id} className="tcf-review-list-item">
                          {req.title} <small style={{ color: '#666' }}>({req.key})</small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <em>No requirements linked</em>
                  )}
                </div>

                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Description</h5>
                  <p className="tcf-review-text">
                    {formData.description || <em>No description provided</em>}
                  </p>
                </div>

                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Test Steps ({stepsArray.length})</h5>
                  {stepsArray.length > 0 ? (
                    <ol className="tcf-review-list">
                      {stepsArray.map(step => (
                        <li key={step.number} className="tcf-review-list-item">
                          {step.text}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <em>No steps provided</em>
                  )}
                </div>

                <div className="tcf-review-section">
                  <h5 className="tcf-review-heading">Expected Result</h5>
                  <p className="tcf-review-text">
                    {formData.expectedResult || (
                      <em>No expected result provided</em>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Footer Actions */}
      <div className="tcf-footer-bar">
        <button
          type="button"
          className="tcf-footer-btn btn-secondary"
          onClick={currentStep === 1 ? onCancel : handlePrevStep}
          disabled={loading}
        >
          {currentStep === 1 ? 'Cancel' : '← Previous'}
        </button>

        {currentStep < totalSteps && (
          <button
            type="button"
            className="tcf-footer-btn btn-primary"
            onClick={handleNextStep}
            disabled={loading}
          >
            Next →
          </button>
        )}

        {currentStep === totalSteps && (
          <button
            type="submit"
            className="tcf-footer-btn btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {testCase ? 'Updating...' : 'Creating...'}
              </>
            ) : testCase ? (
              'Update Test Case'
            ) : (
              'Create Test Case'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
